import React, { useEffect, useMemo, useState } from 'react'
import { Chip, CircularProgress, InputAdornment, TextField, Typography } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning'
import Portlet from 'components/reusable/portlet'
import Connect from 'app/redux/connect'
import { Connected } from 'app/interface/redux'
import { businessManagerService } from 'app/service/business-manager'
import {
  AuthorizerConnectionsByUser,
  AuthorizerTokenStatus,
  BusinessError,
  SystemUserBusinessConnection,
} from '@marketing-milk/interfaces'
import { matchesSystemUserSearch, SystemUserRow } from './SystemUserRows'
import { PersonRow, toPersonBusinessRows } from './PersonRows'
import { businessMatches, normalizeSearch, textMatches } from './search'
import {
  hasCritical,
  hasWarning,
  isFiltering,
  IssueFilter,
  matchesIssueFilter,
  NO_ISSUE_FILTER,
  severityStyle,
} from './errorLabels'

type IdentityView = 'all' | 'system_users' | 'people'

const VIEW_LABELS: Record<IdentityView, string> = {
  all: 'All',
  people: 'Personal logins',
  system_users: 'System users',
}

// DRI-253: every Facebook connection in one place, grouped by the identity that holds it. A business is on one
// identity: our system users, or one person's own Facebook login (the fallback when no system user of ours
// reaches both its ad account and page). People come first: they're the exceptions that need watching, since
// each depends on one person's login. A business on a person keeps its system user ids underneath as the
// target to go back to, so it's listed under the person only, not under that system user as well.
export function FacebookConnectionsPageComponent(props: Connected) {
  const currentUserId = props.core.session.user!.id
  const [systemUsers, setSystemUsers] = useState<SystemUserBusinessConnection[] | null>(null)
  const [people, setPeople] = useState<AuthorizerConnectionsByUser[] | null>(null)
  const [tokenStatuses, setTokenStatuses] = useState<Map<number, AuthorizerTokenStatus>>(new Map())
  const [tokenStatusesLoading, setTokenStatusesLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [issueFilter, setIssueFilter] = useState<IssueFilter>(NO_ISSUE_FILTER)
  const toggleIssueFilter = (key: keyof IssueFilter) =>
    setIssueFilter(current => ({ ...current, [key]: !current[key] }))
  const [view, setView] = useState<IdentityView>('all')

  useEffect(() => {
    businessManagerService
      .getSystemUserBusinesses()
      .then(setSystemUsers)
      .catch(() => setSystemUsers([]))
    businessManagerService
      .getAllAuthorizations()
      .then(setPeople)
      .catch(() => setPeople([]))
    businessManagerService
      .getAuthorizerTokenStatuses()
      .then(statuses => setTokenStatuses(new Map(statuses.map(s => [s.user_id, s]))))
      .catch(() => {})
      .finally(() => setTokenStatusesLoading(false))
  }, [])

  const reloadPeople = () => {
    businessManagerService
      .getAllAuthorizations()
      .then(setPeople)
      .catch(() => {})
  }

  const personBusinessIDs = useMemo(
    () => new Set((people ?? []).flatMap(p => p.connections.map(c => c.business_id))),
    [people]
  )

  // system users with only the businesses they actually connect, i.e. not those on a person
  const systemUserGroups = useMemo(
    () =>
      (systemUsers ?? [])
        .map(su => ({
          ...su,
          connections: su.connections.filter(c => !personBusinessIDs.has(c.business_id)),
        }))
        .filter(su => su.connections.length > 0),
    [systemUsers, personBusinessIDs]
  )

  const visibleSystemUsers = useMemo(() => {
    const q = normalizeSearch(search)
    let list = systemUserGroups
    if (isFiltering(issueFilter)) {
      list = list.filter(c => c.connections.some(a => matchesIssueFilter(a.errors, issueFilter)))
    }
    if (q) list = list.filter(c => matchesSystemUserSearch(c, q))
    return [...list].sort(
      (a, b) =>
        new Set(b.connections.map(c => c.business_id)).size -
        new Set(a.connections.map(c => c.business_id)).size
    )
  }, [systemUserGroups, search, issueFilter])

  const visiblePeople = useMemo(() => {
    const q = normalizeSearch(search)
    let list = people ?? []
    if (isFiltering(issueFilter)) {
      list = list.filter(u =>
        toPersonBusinessRows(u).some(r => matchesIssueFilter(r.errors, issueFilter))
      )
    }
    if (q) {
      list = list.filter(
        u =>
          textMatches(u.user_name, q) ||
          textMatches(u.user_email, q) ||
          u.connections.some(c => businessMatches(c, [c.asset_id], q))
      )
    }
    return [...list].sort(
      (a, b) =>
        new Set(b.connections.map(c => c.business_id)).size -
        new Set(a.connections.map(c => c.business_id)).size
    )
  }, [people, search, issueFilter])

  // each business counted once, under the identity that connects it
  const totals = useMemo(() => {
    const businesses = new Set<number>()
    const broken = new Set<number>()
    const warned = new Set<number>()
    const add = (businessID: number, errors: BusinessError[] | undefined) => {
      businesses.add(businessID)
      if (hasCritical(errors)) broken.add(businessID)
      if (hasWarning(errors)) warned.add(businessID)
    }
    for (const su of systemUserGroups) {
      for (const asset of su.connections) add(asset.business_id, asset.errors)
    }
    for (const person of people ?? []) {
      for (const row of toPersonBusinessRows(person)) add(row.business_id, row.errors)
    }
    return { businesses: businesses.size, errors: broken.size, warnings: warned.size }
  }, [systemUserGroups, people])

  const loading = systemUsers === null || people === null
  const showSystemUsers = view !== 'people'
  const showPeople = view !== 'system_users'
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

  const sectionTitle = (title: string, count: number, help: string) => (
    <div style={{ margin: '16px 0 8px' }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {title} ({count})
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {help}
      </Typography>
    </div>
  )

  return (
    <Portlet
      title="Facebook Connections"
      headTools={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'flex-end',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {!loading && (
            <Typography variant="body2" color="textSecondary">
              {plural((people ?? []).length, 'person', 'people')} ·{' '}
              {plural(systemUserGroups.length, 'system user', 'system users')} ·{' '}
              {plural(totals.businesses, 'business', 'businesses')}
              {totals.errors > 0 && (
                <>
                  {' · '}
                  <span style={{ color: severityStyle.critical.chipText, fontWeight: 600 }}>
                    {plural(totals.errors, 'error', 'errors')}
                  </span>
                </>
              )}
              {totals.warnings > 0 && (
                <>
                  {' · '}
                  <span style={{ color: severityStyle.warning.chipText, fontWeight: 600 }}>
                    {plural(totals.warnings, 'warning', 'warnings')}
                  </span>
                </>
              )}
            </Typography>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            {(Object.keys(VIEW_LABELS) as IdentityView[]).map(v => (
              <Chip
                key={v}
                label={VIEW_LABELS[v]}
                size="small"
                clickable
                color={view === v ? 'primary' : 'default'}
                onClick={() => setView(v)}
              />
            ))}
          </div>
          {(['errors', 'warnings'] as const).map(filter => {
            const style = severityStyle[filter === 'errors' ? 'critical' : 'warning']
            return (
              <Chip
                key={filter}
                icon={<WarningIcon style={{ fontSize: 14 }} />}
                label={filter === 'errors' ? 'Errors only' : 'Warnings only'}
                size="small"
                clickable
                onClick={() => toggleIssueFilter(filter)}
                style={
                  issueFilter[filter]
                    ? { background: style.chipBackground, color: style.chipText, fontWeight: 600 }
                    : undefined
                }
              />
            )
          })}
          <TextField
            placeholder="Search by name or ID: person, system user, BM, business, Ad Account, Page..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            style={{ width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <CircularProgress />
        </div>
      ) : (
        <div>
          {showPeople && (
            <>
              {sectionTitle(
                'Personal logins',
                visiblePeople.length,
                "Someone's own Facebook login connecting both a business's Ad Account and Page, used only where no system user of ours can reach both. It stops working if their login expires."
              )}
              {visiblePeople.length === 0 ? (
                <Typography color="textSecondary" style={{ padding: '0 0 8px' }}>
                  {search || isFiltering(issueFilter)
                    ? 'No personal logins match.'
                    : 'No businesses are connected by a personal login.'}
                </Typography>
              ) : (
                visiblePeople.map(person => (
                  <PersonRow
                    key={person.user_id}
                    authorizer={person}
                    searchQuery={search}
                    currentUserId={currentUserId}
                    issueFilter={issueFilter}
                    tokenStatus={tokenStatuses.get(person.user_id)}
                    tokenStatusLoading={tokenStatusesLoading}
                    onErrorsCleared={reloadPeople}
                  />
                ))
              )}
            </>
          )}
          {showSystemUsers && (
            <>
              {sectionTitle(
                'System users',
                visibleSystemUsers.length,
                'Our automated identities, one per Facebook Business Manager. The default for every business.'
              )}
              {visibleSystemUsers.length === 0 ? (
                <Typography color="textSecondary" style={{ padding: '0 0 8px' }}>
                  {search || isFiltering(issueFilter)
                    ? 'No system users match.'
                    : 'No Facebook Builder system users found.'}
                </Typography>
              ) : (
                visibleSystemUsers.map(connection => (
                  <SystemUserRow
                    key={connection.system_user_id}
                    connection={connection}
                    searchQuery={search}
                    issueFilter={issueFilter}
                  />
                ))
              )}
            </>
          )}
        </div>
      )}
    </Portlet>
  )
}

export const FacebookConnectionsPage = Connect(FacebookConnectionsPageComponent)
